/**
 * Save contract for the Details step — the `useImperativeHandle` that
 * exposes `{ validate, syncToPayload }` to the wizard shell.
 *
 * Extracted verbatim from `DetailsStep` (pure file separation — no
 * behaviour change). The factory body and the exact dependency array
 * are unchanged; only their location moved. Called at the same
 * hook-order position in `DetailsStep` so React's hook order is
 * preserved.
 */
import { useImperativeHandle, type MutableRefObject, type Ref } from 'react';
import type { FormBuilderHandle } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { useShowToast } from '@/hooks';
import type { ScheduleInterviewPayload } from '../../../config/types';
import type { ScheduleSharedState } from '../../../state/useScheduleSharedState';
import type { FindTimeValue } from '../../../calendar/selfSchedule';
import { getCachedInterviewerOption } from '../../../state/interviewerOptionCache';
import { requiresNoteTakerEnabled } from '@/formConfigs/useNoteTakerConfig';
import {
  isInPast,
  isFootprintStartInPast,
  isFootprintBeforeEarliestBookable,
} from '../../../calendar/events/eventHelpers';
import type { InterviewerWithoutTimezone } from '../timezoneModal';

export interface ScheduleContextSnapshot {
  workingHoursByInterviewer: Record<
    string,
    Array<{ start: string; end: string }>
  >;
  busyEvents: ReadonlyArray<{
    id: string;
    start: string;
    end: string;
    interviewerId: string | number;
  }>;
  isReady: boolean;
}

interface UseDetailsStepSaveArgs {
  ref: Ref<StepFormApi>;
  formRef: React.RefObject<FormBuilderHandle | null>;
  payloadRef: MutableRefObject<ScheduleInterviewPayload>;
  tzModalRef: React.RefObject<{
    open: (_interviewers: InterviewerWithoutTimezone[]) => void;
  } | null>;
  scheduleContextRef: MutableRefObject<ScheduleContextSnapshot>;
  sharedState: ScheduleSharedState;
  isSelfSchedule: boolean;
  isAdhoc: boolean;
  isBufferEnabledByFlag: boolean;
  /**
   * Self-schedule minimum-notice lead (hours). Self-schedule slots must
   * sit beyond `now + minimumNoticeHours`; used to flag slots that have
   * drifted into the notice window while the recruiter waited. Manual
   * mode doesn't carry a notice (the BE applies it to `time_slots` only).
   */
  minimumNoticeHours: number;
  interviewPlan?: Record<string, unknown>;
  findTimeValue: FindTimeValue;
  toast: ReturnType<typeof useShowToast>;
  /**
   * Account/job mandates an AI note-taker for every interview. The
   * note-taker form field is conditional on `location ∈ {zoom,
   * google_meet, teams}` — outside that set the field is hidden, its
   * `required: true` is bypassed, and the BE later rejects the save
   * with `err_note_taker_mandatory`. Validate the combination here so
   * the recruiter sees the conflict on step 1 instead of after going
   * through Communication + Scorecard and hitting a toast on save.
   */
  isNoteTakerMandatory: boolean;
}

export function useDetailsStepSave({
  ref,
  formRef,
  payloadRef,
  tzModalRef,
  scheduleContextRef,
  sharedState,
  isSelfSchedule,
  isAdhoc,
  isBufferEnabledByFlag,
  minimumNoticeHours,
  interviewPlan,
  findTimeValue,
  toast,
  isNoteTakerMandatory,
}: UseDetailsStepSaveArgs): void {
  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        if (!formRef.current) return true;
        const slot = sharedState.bookedSlot;
        const needsSlot = !isSelfSchedule && (!slot?.start || !slot?.end);
        // Self-schedule's analogue: at least one picked slot
        // before moving on. Mirrors manual's toast so the user
        // gets the same failure signal on both modes.
        const needsSelfSlots =
          isSelfSchedule && (sharedState.selfScheduleSlots?.length ?? 0) === 0;
        // Self-schedule guard against the empty-snapshot race
        // (G1, send-additional-slots): if the user clicks Save/Next
        // before CalendarPanel's busy + working-hours queries have
        // settled, every newly drawn slot would classify as
        // `override_type: 'none'` regardless of where it sits on
        // the grid — making backend filter rejections invisible to
        // the recruiter. Block the transition with a toast so they
        // can retry once the data lands. Skip the block when every
        // slot is `_fromServer: true` (edit-mode hydration where
        // the user touched nothing else on the calendar) — those
        // pass through verbatim in `mapDetails` and don't need a
        // fresh classifier.
        const hasUnclassifiedSlot =
          isSelfSchedule &&
          (sharedState.selfScheduleSlots ?? []).some((s) => !s._fromServer);
        const needsScheduleContext =
          isSelfSchedule &&
          !needsSelfSlots &&
          hasUnclassifiedSlot &&
          !scheduleContextRef.current.isReady;
        // Self-schedule mandates a timezone for every picked
        // interviewer — the candidate-facing app reads each
        // interviewer's timezone when rendering their availability,
        // and a blank value collapses to UTC which silently shifts
        // every slot off the recruiter's intended grid. Open the
        // bulk-fix modal here (legacy `SchInterviewerTimezoneModal`
        // parity) pre-populated with everyone missing a tz; the
        // per-chip globe path opens the same modal with a single
        // entry.
        const missingTzInterviewers: InterviewerWithoutTimezone[] = (() => {
          if (!isSelfSchedule) return [];
          const ids = sharedState.interviewerIds ?? [];
          if (ids.length === 0) return [];
          const opts = (payloadRef.current.details as Record<string, unknown>)
            ?.participant_default_options as
            | Array<{
                value: string;
                label: string;
                image?: string;
                data?: { timezone?: string };
              }>
            | undefined;
          const out: InterviewerWithoutTimezone[] = [];
          for (const rawId of ids) {
            const id = String(rawId);
            const seed = Array.isArray(opts)
              ? opts.find((o) => String(o.value) === id)
              : undefined;
            const cached = getCachedInterviewerOption(id);
            const tz =
              (seed?.data?.timezone && seed.data.timezone.trim()) ||
              (cached?.timezone && cached.timezone.trim()) ||
              '';
            if (!tz) {
              out.push({
                id: Number(id),
                name: seed?.label || cached?.label || `User ${id}`,
                avatar: seed?.image || cached?.image,
              });
            }
          }
          return out;
        })();
        const needsTimezone = missingTzInterviewers.length > 0;
        // Legacy-parity past-time guard. Editing a past interview leaves
        // `bookedSlot.start` / hydrated self-schedule slots in the past;
        // saving without a fresh time round-trips a BE 422
        // (`err_start_time_less_than_current_time`). Surface it here so
        // the recruiter sees the failure on step 1 instead of after
        // walking through Communication + Scorecard.
        //
        // Buffer-aware: the `[before · event · after]` block is validated
        // as one unit, so we check the FOOTPRINT start (slot start minus
        // the before-buffer). The before-buffer is the earliest edge and
        // slips into the past first — e.g. a slot picked just after 1pm
        // drifts invalid a few minutes later, while the recruiter waits.
        // Effective buffer is 0 when the flag or the user toggle is off,
        // so this reduces to a plain `slot.start` past-check then.
        const effectiveBeforeMinutes =
          isBufferEnabledByFlag && sharedState.bufferEnabled
            ? sharedState.bufferBeforeMinutes
            : 0;
        // Distinguish the two failure shapes so the toast can name the
        // right culprit: the booked time itself is in the past, vs the
        // time is still future but its before-buffer has slipped past
        // (e.g. the recruiter bumped the buffer up after picking).
        const bookingStartInPast = Boolean(
          !isSelfSchedule &&
          slot?.start &&
          isInPast(slot.start, sharedState.timezone)
        );
        const bookingBufferInPast = Boolean(
          !isSelfSchedule &&
          slot?.start &&
          !bookingStartInPast &&
          isFootprintStartInPast(
            slot.start,
            effectiveBeforeMinutes,
            sharedState.timezone
          )
        );
        const needsFutureBooking = bookingStartInPast || bookingBufferInPast;
        // Self-schedule additionally rejects slots inside the minimum-
        // notice window (`now + notice`), not just the past — a slot
        // created at the notice edge drifts into that window as time
        // passes, and the BE 422s it (`err_slot_in_notice_time`). Check
        // the footprint against `now + notice` so the toast fires before
        // the save. `noticeHours = 0` reduces to the plain past-check.
        const needsFutureSelfSlots = Boolean(
          isSelfSchedule &&
          !needsSelfSlots &&
          (sharedState.selfScheduleSlots ?? []).some((s) =>
            isFootprintBeforeEarliestBookable(
              s.start,
              effectiveBeforeMinutes,
              minimumNoticeHours,
              sharedState.timezone
            )
          )
        );
        // Calendar-not-connected block — DISABLED per product (sch-56).
        // We no longer block scheduling when a selected interviewer hasn't
        // connected their calendar. Kept commented so it can be re-enabled
        // if product reverses. To restore: uncomment this block, the
        // `!needsConnectedCalendar` guard in the return below, and the
        // `calendarDisconnectedInterviewerIds` dep in the deps array.
        // const disconnectedSelectedNames: string[] = (() => {
        //   const selected = (sharedState.interviewerIds ?? []).map(String);
        //   const disconnected = new Set(
        //     (sharedState.calendarDisconnectedInterviewerIds ?? []).map(String)
        //   );
        //   if (selected.length === 0 || disconnected.size === 0) return [];
        //   const opts = (payloadRef.current.details as Record<string, unknown>)
        //     ?.participant_default_options as
        //     | Array<{ value: string; label: string }>
        //     | undefined;
        //   const names: string[] = [];
        //   for (const id of selected) {
        //     if (!disconnected.has(id)) continue;
        //     const seed = Array.isArray(opts)
        //       ? opts.find((o) => String(o.value) === id)
        //       : undefined;
        //     const cached = getCachedInterviewerOption(id);
        //     names.push(seed?.label || cached?.label || `User ${id}`);
        //   }
        //   return names;
        // })();
        // const needsConnectedCalendar = disconnectedSelectedNames.length > 0;
        // if (needsConnectedCalendar) {
        //   const isPlural = disconnectedSelectedNames.length > 1;
        //   toast({
        //     id: 'schedule-interview-calendar-not-connected',
        //     title: 'Interviewer calendar not connected',
        //     description: `${disconnectedSelectedNames.join(', ')} ${
        //       isPlural ? "haven't" : "hasn't"
        //     } connected their calendar. Connect it to continue.`,
        //     status: 'error',
        //   });
        // } else
        if (needsSlot) {
          toast({
            id: 'schedule-interview-no-slot',
            title: 'Pick a time slot on the calendar',
            status: 'error',
          });
        } else if (needsSelfSlots) {
          toast({
            id: 'schedule-interview-no-self-slots',
            title: 'Pick at least one slot on the calendar',
            status: 'error',
          });
        } else if (needsFutureBooking) {
          toast({
            id: 'schedule-interview-past-slot',
            title: bookingBufferInPast
              ? "Selected Event slot's buffer is in past"
              : 'Selected Event slot is in past',
            status: 'error',
          });
        } else if (needsFutureSelfSlots) {
          toast({
            id: 'schedule-interview-past-self-slots',
            title:
              'One or more slots are in the past or within the minimum notice period. Pick different slots.',
            status: 'error',
          });
        } else if (needsTimezone) {
          tzModalRef.current?.open(missingTzInterviewers);
        } else if (needsScheduleContext) {
          toast({
            id: 'schedule-interview-context-loading',
            title: 'Loading availability, please try again in a moment',
            status: 'error',
          });
        }
        const isFormValid = await formRef.current.trigger();
        return (
          isFormValid &&
          // !needsConnectedCalendar &&
          !needsSlot &&
          !needsSelfSlots &&
          !needsFutureBooking &&
          !needsFutureSelfSlots &&
          !needsTimezone &&
          !needsScheduleContext
        );
      },
      syncToPayload: () => {
        if (!formRef.current) return;
        const formValues = formRef.current.getValues();
        // When the flag is on, emit the full buffer triplet
        // (including `bufferEnabled`) so BE receives toggle-off
        // intent. The BE shape (kula/core CORE-4350,
        // `interviewer_buffer_{enabled,pre_minutes,post_minutes}`)
        // needs `enabled` explicitly — otherwise toggling OFF in the
        // wizard would silently keep buffer on server-side.
        const shouldEmitBuffer = isBufferEnabledByFlag;
        // Edit-mode `interviewPlan === existingInterview` (the raw
        // GET response), so its `meeting_rooms` is the
        // server-current room list. mapDetails uses it to diff
        // against `meeting_room_ids` and emit `_destroy: true` for
        // removed entries — the parity gap audit caught this gap
        // (legacy did the diff; the new wizard was only emitting
        // additions, so removed rooms stayed attached server-side).
        // Create mode → no `meeting_rooms` on the plan → mapDetails
        // skips deletion logic and behaves as before.
        const originalMeetingRooms = Array.isArray(
          (interviewPlan as { meeting_rooms?: unknown })?.meeting_rooms
        )
          ? ((interviewPlan as { meeting_rooms: unknown[] })
              .meeting_rooms as Array<{
              id?: number | string;
              email?: string;
              name?: string;
            }>)
          : [];
        // Unchecking "Book a meeting room" must clear the selection so
        // mapDetails diffs `meeting_room_ids: []` against the server's
        // originals and emits `_destroy: true` for each — otherwise the
        // hidden field keeps its last value and the booked rooms (and
        // their calendar events) silently survive the edit.
        const bookMeetingRoom = (formValues as Record<string, unknown>)
          ?.book_meeting_room;
        payloadRef.current.details = {
          ...formValues,
          ...(bookMeetingRoom === false ? { meeting_room_ids: [] } : {}),
          // Mandate guard — narrowed to virtual locations: the BE only
          // raises `err_note_taker_mandatory` when location ∈
          // {zoom, google_meet, teams} AND `note_taker_enabled: false`
          // is sent on a mandated job. For non-virtual locations the BE
          // silently forces note_taker_enabled=false and accepts, so
          // forcing `true` here would just be ignored. Force `true`
          // only on the path that would otherwise trigger the BE
          // rejection — covers the Redux-late-load race where the
          // mandate seed in `useWizardInitialValues` missed because
          // `currentJob.data.note_taker.mandatory` hadn't loaded yet.
          ...(requiresNoteTakerEnabled(
            isNoteTakerMandatory,
            (formValues as Record<string, unknown> | undefined)?.location
          )
            ? { enable_note_taker: true }
            : {}),
          bookedSlot: sharedState.bookedSlot,
          selfScheduleSlots: sharedState.selfScheduleSlots,
          timezone: sharedState.timezone,
          // Adhoc has no plan to opt-in from. Mirror the schema
          // gate in `useScheduleCalendarData` — picking a calendar
          // in adhoc IS the opt-in, so mapDetails should ship
          // `calendar_info` to the BE.
          shared_calendar_enabled:
            isAdhoc || Boolean(interviewPlan?.shared_calendar_enabled),
          // Round-tripped into `schedule_type` by the payload
          // builder (`scheduleTypeFor`). `null` → 'Custom'.
          findTimeValue,
          // Plan-level meeting-rooms toggle — drives the "feature
          // disabled → wipe all rooms" branch in mapDetails.
          meeting_rooms_enabled:
            (interviewPlan as { meeting_rooms_enabled?: unknown })
              ?.meeting_rooms_enabled === undefined
              ? true
              : Boolean(
                  (interviewPlan as { meeting_rooms_enabled?: unknown })
                    .meeting_rooms_enabled
                ),
          // Snapshot of server-side rooms — UI-only key consumed by
          // mapDetails for the deletion diff. Stripped before wire
          // because mapDetails builds `mapped` key-by-key.
          _originalMeetingRooms: originalMeetingRooms,
          // Email → display-name lookup so mapDetails can stamp the
          // real room name onto `meeting_rooms_attributes[].name`
          // when adding a new room. Without this the mapper falls
          // back to using the email as the name, which the backend
          // stores verbatim and shows in upcoming-interview lists.
          _meetingRoomNamesByEmail: sharedState.meetingRoomNamesByEmail,
          // Self-schedule only — snapshot the override-classifier
          // inputs into the details payload so `mapDetails` can
          // compute `is_custom` / `override_type` per slot at save
          // time. `_overrideContext` is a UI-only key; the payload
          // builder reads + strips it before serialising. See
          // `payload/computeSlotOverride.ts` for the algorithm.
          //
          // Only emit when the snapshot is ready (busy + WH queries
          // have settled). When not ready, `validate()` above has
          // already blocked the transition with a toast, so this is
          // belt-and-suspenders: even if a caller bypassed validate,
          // mapDetails falls through to the slot's stored fields
          // instead of computing against empty data.
          ...(isSelfSchedule && scheduleContextRef.current.isReady
            ? {
                _overrideContext: {
                  workingHoursByInterviewer:
                    scheduleContextRef.current.workingHoursByInterviewer,
                  busyEvents: scheduleContextRef.current.busyEvents,
                  interviewerIds: sharedState.interviewerIds,
                  interviewKind: sharedState.interviewKind,
                },
              }
            : {}),
          ...(shouldEmitBuffer
            ? {
                bufferEnabled: sharedState.bufferEnabled,
                bufferBeforeMinutes: sharedState.bufferBeforeMinutes,
                bufferAfterMinutes: sharedState.bufferAfterMinutes,
              }
            : {}),
        };
      },
    }),
    [
      // `formRef` / `scheduleContextRef` / `tzModalRef` are stable ref
      // objects (were component-local useRefs pre-extraction, so the
      // original deps array omitted them); listed now only to satisfy
      // exhaustive-deps across the hook boundary — identities never
      // change so the imperative handle behaves exactly as before.
      payloadRef,
      formRef,
      scheduleContextRef,
      tzModalRef,
      sharedState.bookedSlot,
      sharedState.selfScheduleSlots,
      sharedState.timezone,
      sharedState.bufferEnabled,
      sharedState.bufferBeforeMinutes,
      sharedState.bufferAfterMinutes,
      sharedState.interviewerIds,
      // sharedState.calendarDisconnectedInterviewerIds,
      sharedState.interviewKind,
      sharedState.meetingRoomNamesByEmail,
      interviewPlan,
      isSelfSchedule,
      isAdhoc,
      isBufferEnabledByFlag,
      minimumNoticeHours,
      toast,
      findTimeValue,
      isNoteTakerMandatory,
    ]
  );
}
